import React, { createContext, Dispatch, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { dndElements, DndElementsType } from './Editor/dndElements'
import { currentFieldConfig, UiFromSpecFieldConfig } from './Editor/FieldConfig/fieldConfig'
import { CustomEditElementType } from './Editor/EditInput/EditInput'
import { getElementByKey } from './FormRenderer/utils/getElementByKey'
import { useHistory } from './hooks/useHistory'
import { CustomDisplayNameLayerType } from './Editor/Panels/utils/getDisplayName'
import { DropResult } from './Editor/Panels/LayerPanel'

function deepCopy(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj // Jika bukan objek atau array, kembalikan nilai asli
  }

  if (Array.isArray(obj)) {
    return obj.map(deepCopy) // Salin setiap elemen dalam array
  }

  const copiedObj: any = {}
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      copiedObj[key] = deepCopy(obj[key]) // Salin properti secara rekursif
    }
  }
  return copiedObj
}

export type CustomTranslations = {
  en?: any
  id?: any
}

export type DesignForm = {
  backgroundColor?: string
  textColor?: string
  fontFamily?: string
  fontSize?: number
  textAlign?: 'left' | 'center' | 'right' | 'justify'
  textFormat?: ('bold' | 'italic' | 'underline')[]
}

export type customReOrder = {
  item: any
  dropResult: DropResult
  selectedElements?: any[]
  fromIndex: string[]
  toIndex: string[]
  defaultReOrder: (fromIndex: string[], toIndex: string[]) => void
  defaultMultipleReOrder: (selectedElements: string[][], toIndex: string[]) => void
  parentComponent?: any
  index?: number
}

export type UiFromSpecContextType = {
  editor: boolean
  setEditor: (value: boolean) => void
  propertyEditor?: boolean
  setPropertyEditor?: (value: boolean) => void
  currentSpec: any[]
  setCurrentSpecWithHistory: (actionValue: any) => void
  designState: DesignForm
  setDesignState: (value: DesignForm) => void
  selectedPath?: any
  setSelectedPath?: (value: any) => void
  selectedElement?: any
  setSelectedElement?: (value: any) => void
  selectedElements?: any[]
  setSelectedElements?: (value: any[]) => void
  appendNewElementToSpec: (newElement: any, path: string[], index?: number) => void
  removeElementFromSpec: (path: string[]) => void
  updateElementInSpec: (path: string[], newElement: any) => void
  reorderElementsInSpec: (fromIndex: string[], toIndex: string[]) => void
  reorderMultipleElementsInSpec: (fromIndices: string[][], toIndex: string[]) => void
  undo?: () => void
  redo?: () => void
  canUndo?: boolean
  canRedo?: boolean
  show?: boolean
  setShow?: Dispatch<React.SetStateAction<boolean>>
  language?: 'id' | 'en'
  setLanguage?: Dispatch<React.SetStateAction<'id' | 'en'>>
  fieldConfig?: UiFromSpecFieldConfig[]
  dndElements?: DndElementsType[]
  customEditElement?: CustomEditElementType
  customDisplayNameLayer?: CustomDisplayNameLayerType
  customIconLayer?: (uiType: string, defaultIcon: React.ReactNode) => React.ReactNode
  customReOrder?: (params: customReOrder) => void
  cannotDragElements?: string[]
  disableLayerDropper?: string[]
  customDropper?: string[]
  disableSaveEdit?: boolean
  setDisableSaveEdit?: Dispatch<React.SetStateAction<boolean>>
  customTranslations?: CustomTranslations
}

export type UiFromSpecProviderProps = {
  children: React.ReactNode
  editorState?: boolean
  propertyEditorState?: boolean
  currentSpecState?: any[]
  overrideFieldConfig?: UiFromSpecFieldConfig[]
  overrideDndElements?: DndElementsType[]
  customEditElement?: CustomEditElementType
  customDisplayNameLayer?: CustomDisplayNameLayerType
  customIconLayer?: (uiType: string, defaultIcon: React.ReactNode) => React.ReactNode
  customDropperUiType?: string[]
  customReOrderElementsLayer?: (params: customReOrder) => void
  cannotDrag?: string[]
  disableLayerDropperBasedOnUiType?: string[]
  language?: 'id' | 'en'
  customTranslations?: CustomTranslations // New prop for custom translations
}

const UiFromSpecContext = createContext<UiFromSpecContextType | undefined>(undefined)

export const UiFromSpecProvider: React.FC<UiFromSpecProviderProps> = ({
  children,
  editorState,
  propertyEditorState,
  currentSpecState,
  overrideFieldConfig = [],
  overrideDndElements = [],
  customEditElement,
  customDisplayNameLayer,
  customReOrderElementsLayer,
  customIconLayer,
  customDropperUiType = [],
  cannotDrag = [],
  disableLayerDropperBasedOnUiType = [],
  language: languageProp,
  customTranslations,
}) => {
  const [editor, setEditor] = useState(editorState || false)
  const [propertyEditor, setPropertyEditor] = useState(propertyEditorState || false)
  const {
    state: currentSpec,
    set: setCurrentSpecWithHistory,
    reset,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory(currentSpecState || [])
  const [selectedPath, setSelectedPath] = useState<any>()
  const [selectedElement, setSelectedElement] = useState<any>()
  const [selectedElements, setSelectedElements] = useState<any>([])
  const [disableSaveEdit, setDisableSaveEdit] = useState<any>(false)
  const [show, setShow] = useState(false)
  const [language, setLanguage] = useState<'id' | 'en'>(languageProp ?? 'en')
  const cannotDragElements = ['MAIN_DIV', ...cannotDrag]
  const disableLayerDropper = ['FORM2X3', 'FORM2X2', ...disableLayerDropperBasedOnUiType]
  const customDropper = ['DIV', 'MAIN_DIV', 'dropper', ...customDropperUiType]

  // Gunakan useRef untuk melacak nilai props sebelumnya
  const prevCurrentSpecStateRef = useRef<any[]>()

  // Sync with prop changes
  useEffect(() => {
    // Hanya panggil reset jika prop `currentSpecState` benar-benar berubah
    // dari satu render ke render berikutnya. Ini mencegah loop.
    if (currentSpecState !== prevCurrentSpecStateRef.current) {
      if (currentSpecState !== undefined) {
        reset(currentSpecState)
      }
    }

    // Selalu update ref dengan nilai prop saat ini untuk render berikutnya.
    prevCurrentSpecStateRef.current = currentSpecState
  }, [currentSpecState, reset])

  useEffect(() => {
    if (editorState !== undefined) {
      setEditor(editorState)
    }
  }, [editorState])

  useEffect(() => {
    if (propertyEditorState !== undefined) {
      setPropertyEditor(propertyEditorState)
    }
  }, [propertyEditorState])

  useEffect(() => {
    if (languageProp !== undefined) {
      setLanguage(languageProp)
    }
  }, [languageProp])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent shortcuts if editor is on
      if (!editor) {
        return
      }

      // Deteksi apakah OS adalah Mac
      const isMac = navigator?.platform?.toUpperCase()?.indexOf('MAC') >= 0
      const undoKeyPressed = event?.key?.toLowerCase() === 'z'

      // Cek shortcut untuk UNDO (Ctrl+Z atau Cmd+Z)
      if (
        undoKeyPressed &&
        !event.shiftKey && // Pastikan Shift TIDAK ditekan
        (isMac ? event.metaKey : event.ctrlKey)
      ) {
        event.preventDefault() // Hentikan aksi default browser
        if (canUndo) {
          undo()
        }
      }

      // Cek shortcut untuk REDO (Ctrl+Shift+Z atau Cmd+Shift+Z)
      if (
        undoKeyPressed &&
        event.shiftKey && // Pastikan Shift DITEKAN
        (isMac ? event.metaKey : event.ctrlKey)
      ) {
        event.preventDefault() // Hentikan aksi default browser
        if (canRedo) {
          redo()
        }
      }
    }

    // Tambahkan event listener ke window
    window.addEventListener('keydown', handleKeyDown)

    // Fungsi cleanup: hapus event listener saat komponen unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [undo, redo, canUndo, canRedo, editor])

  const designState = useMemo(() => {
    const topLevelElement = getElementByKey(currentSpec, '0')
    return {
      ...topLevelElement?.design,
    } as DesignForm
  }, [currentSpec])

  const setDesignState = useCallback(
    (value: DesignForm) => {
      setCurrentSpecWithHistory((prevSpec: any) => {
        const updatedSpec = deepCopy(prevSpec)
        const topLevelElement = getElementByKey(updatedSpec, '0')
        if (topLevelElement) {
          topLevelElement.design = {
            ...topLevelElement.design,
            ...value,
          }
          return updatedSpec // Kembalikan state yang sudah diperbarui
        }
        return prevSpec // Kembalikan state lama jika tidak ditemukan
      })
    },
    [setCurrentSpecWithHistory]
  )

  const reorderElementsInSpec = useCallback(
    (fromIndex: string[], toIndex: string[]) => {
      setCurrentSpecWithHistory((prevSpec: any) => {
        if (!fromIndex || !toIndex || fromIndex.length === 0 || toIndex.length === 0) {
          console.error('Invalid indices provided for reordering')
          return
        }

        const updatedSpec = deepCopy(prevSpec)

        // Get the element from fromIndex position
        let sourceParent = updatedSpec
        let sourceIndex = -1

        // Navigate to the source element's parent
        for (let i = 0; i < fromIndex.length - 1; i++) {
          const pathIndex = parseInt(fromIndex[i], 10)

          if (isNaN(pathIndex) || pathIndex < 0 || !Array.isArray(sourceParent)) {
            console.error('Invalid fromIndex path:', fromIndex)
            return
          }

          if (!sourceParent[pathIndex].children) {
            sourceParent[pathIndex].children = []
          }
          sourceParent = sourceParent[pathIndex].children
        }

        // Get the source element and its index
        sourceIndex = parseInt(fromIndex[fromIndex.length - 1], 10)
        if (isNaN(sourceIndex) || sourceIndex < 0 || sourceIndex >= sourceParent.length) {
          console.error('Invalid source index:', sourceIndex)
          return
        }

        // Get the target parent
        let targetParent = updatedSpec
        for (let i = 0; i < toIndex.length - 1; i++) {
          const pathIndex = parseInt(toIndex[i], 10)

          if (isNaN(pathIndex) || pathIndex < 0 || !Array.isArray(targetParent)) {
            console.error('Invalid toIndex path:', toIndex)
            return
          }

          if (!targetParent[pathIndex].children) {
            targetParent[pathIndex].children = []
          }
          targetParent = targetParent[pathIndex].children
        }

        const targetIndex = parseInt(toIndex[toIndex.length - 1], 10)
        if (isNaN(targetIndex) || targetIndex < 0) {
          console.error('Invalid target index:', targetIndex)
          return
        }

        // Check if moving within the same parent
        if (sourceParent === targetParent) {
          // Remove element from source position
          const [movedElement] = sourceParent.splice(sourceIndex, 1)

          // Adjust target index if source was before target in the same array
          const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex

          // Insert element at target position
          sourceParent.splice(adjustedTargetIndex, 0, movedElement)
        } else {
          // Moving between different parents
          // Remove from source
          const [movedElement] = sourceParent.splice(sourceIndex, 1)

          // Insert at target
          targetParent.splice(targetIndex, 0, movedElement)
        }

        setSelectedElements([])
        return updatedSpec
      })
    },
    [setCurrentSpecWithHistory, setSelectedElements]
  )

  const reorderMultipleElementsInSpec = useCallback(
    (fromIndices: string[][], toIndex: string[]) => {
      setCurrentSpecWithHistory((prevSpec: any) => {
        if (!fromIndices || fromIndices.length === 0 || !toIndex || toIndex.length === 0) {
          console.error('Invalid indices provided for reordering')
          return prevSpec
        }

        const updatedSpec = deepCopy(prevSpec)

        // Get the target parent from the original spec structure first
        let targetParent = updatedSpec
        for (let i = 0; i < toIndex.length - 1; i++) {
          const pathIndex = parseInt(toIndex[i], 10)
          if (isNaN(pathIndex) || pathIndex < 0 || !Array.isArray(targetParent) || !targetParent[pathIndex]) {
            console.error('Invalid toIndex path:', toIndex)
            return prevSpec
          }
          if (!targetParent[pathIndex].children) {
            targetParent[pathIndex].children = []
          }
          targetParent = targetParent[pathIndex].children
        }

        const targetIndex = parseInt(toIndex[toIndex.length - 1], 10)
        if (isNaN(targetIndex) || targetIndex < 0) {
          console.error('Invalid target index:', targetIndex)
          return prevSpec
        }

        // Sort fromIndices in descending order to avoid index shifting issues during removal
        const sortedFromIndices = [...fromIndices].sort((a, b) => {
          const len = Math.max(a.length, b.length)
          for (let i = 0; i < len; i++) {
            const aVal = i < a.length ? parseInt(a[i], 10) : -Infinity
            const bVal = i < b.length ? parseInt(b[i], 10) : -Infinity
            if (aVal !== bVal) {
              return bVal - aVal
            }
          }
          return 0
        })

        const movedElements: any[] = []
        let moveTheSameParent = true
        let sourceIndexes: number[] = []

        // Remove elements from source positions
        sortedFromIndices.forEach((fromIndex) => {
          let sourceParent = updatedSpec
          for (let i = 0; i < fromIndex.length - 1; i++) {
            const pathIndex = parseInt(fromIndex[i], 10)
            if (isNaN(pathIndex) || pathIndex < 0 || !Array.isArray(sourceParent) || !sourceParent[pathIndex]) {
              console.error('Invalid fromIndex path:', fromIndex)
              return
            }
            sourceParent = sourceParent[pathIndex].children
          }

          const sourceIndex = parseInt(fromIndex[fromIndex.length - 1], 10)
          sourceIndexes.push(sourceIndex)
          if (
            isNaN(sourceIndex) ||
            sourceIndex < 0 ||
            !Array.isArray(sourceParent) ||
            sourceIndex >= sourceParent.length
          ) {
            console.error('Invalid source index:', sourceIndex, 'in path:', fromIndex)
            return
          }

          if (sourceParent !== targetParent) {
            moveTheSameParent = false
          }

          const [movedElement] = sourceParent.splice(sourceIndex, 1)
          movedElements.unshift(movedElement) // Add to the beginning to maintain original order
        })

        if (movedElements.length === 0) {
          return prevSpec // No elements were moved, return original spec
        }

        // Check if moving within the same parent
        if (moveTheSameParent) {
          // Adjust target index if source was before target in the same array
          const adjustedTargetIndex = sourceIndexes[0] < targetIndex ? targetIndex - sourceIndexes.length : targetIndex

          // Insert element at target position
          targetParent.splice(adjustedTargetIndex, 0, ...movedElements)
        } else {
          // Insert at target
          targetParent.splice(targetIndex, 0, ...movedElements)
        }

        setSelectedElements([])
        return updatedSpec
      })
    },
    [setCurrentSpecWithHistory, setSelectedElements]
  )

  const appendNewElementToSpec = useCallback(
    (newElement: any, path: string[], index?: number) => {
      setCurrentSpecWithHistory((prevSpec: any) => {
        // `prevSpec` dijamin adalah versi terbaru dari state
        const updatedSpec = deepCopy(prevSpec)
        let current = updatedSpec

        for (let i = 0; i < path.length; i++) {
          const pathIndex = parseInt(path[i], 10)
          if (isNaN(pathIndex) || pathIndex < 0 || !Array.isArray(current)) {
            console.error('Invalid path:', path)
            return prevSpec // Kembalikan state lama jika path tidak valid
          }
          if (i === path.length - 1) {
            if (!current[pathIndex].children) {
              current[pathIndex].children = []
            }
            if (index !== undefined) {
              current[pathIndex].children.splice(index, 0, deepCopy(newElement))
            } else {
              current[pathIndex].children.push(deepCopy(newElement))
            }
          } else {
            current = current[pathIndex].children
          }
        }

        return updatedSpec // Kembalikan state yang sudah diperbarui
      })
    },
    [setCurrentSpecWithHistory]
  )

  const removeElementFromSpec = useCallback(
    (path: string[]) => {
      setCurrentSpecWithHistory((prevSpec: any) => {
        const updatedSpec = deepCopy(prevSpec) // Salin currentSpec untuk menghindari mutasi langsung
        let current = updatedSpec
        let parent: any = null
        let indexToRemove: number | null = null
        for (let i = 0; i < path.length; i++) {
          const index = parseInt(path[i], 10)
          if (isNaN(index) || index < 0 || !Array.isArray(current)) {
            console.error('Invalid path:', path)
            return
          }
          if (i === path.length - 1) {
            // Jika ini adalah elemen terakhir dalam path, simpan parent dan index untuk dihapus
            parent = current
            indexToRemove = index
          } else {
            // Navigasi ke elemen berikutnya
            parent = current
            current = current[index].children
          }
        }

        if (parent && indexToRemove !== null) {
          parent.splice(indexToRemove, 1) // Hapus elemen dari parent
        }
        return updatedSpec // Kembalikan state yang sudah diperbarui
      })
    },
    [setCurrentSpecWithHistory]
  )

  const updateElementInSpec = useCallback(
    (path: string[], newElement: any) => {
      setCurrentSpecWithHistory((prevSpec: any) => {
        const updatedSpec = deepCopy(prevSpec) // Salin currentSpec untuk menghindari mutasi langsung
        let current = updatedSpec
        for (let i = 0; i < path.length; i++) {
          const index = parseInt(path[i], 10)
          if (isNaN(index) || index < 0 || !Array.isArray(current)) {
            console.error('Invalid path:', path)
            return
          }
          if (i === path.length - 1) {
            // Jika ini adalah elemen terakhir dalam path, perbarui elemen
            current[index] = { ...current[index], ...newElement }
          } else {
            // Navigasi ke elemen berikutnya
            current = current[index].children
          }
        }
        return updatedSpec // Kembalikan state yang sudah diperbarui
      })
    },
    [setCurrentSpecWithHistory]
  )

  // Memoize computed arrays to prevent unnecessary re-renders
  const memoizedFieldConfig = useMemo(() => {
    return [...currentFieldConfig, ...overrideFieldConfig]
  }, [overrideFieldConfig])

  const memoizedDndElements = useMemo(() => {
    return overrideDndElements
    
  }, [overrideDndElements])

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      editor,
      setEditor,
      propertyEditor,
      setPropertyEditor,
      currentSpec,
      setCurrentSpecWithHistory,
      selectedPath,
      setSelectedPath,
      appendNewElementToSpec,
      removeElementFromSpec,
      reorderElementsInSpec,
      reorderMultipleElementsInSpec,
      undo,
      redo,
      reset,
      canUndo,
      canRedo,
      selectedElement,
      setSelectedElement,
      selectedElements,
      setSelectedElements,
      show,
      setShow,
      language,
      setLanguage,
      designState,
      setDesignState,
      updateElementInSpec,
      customEditElement,
      customDisplayNameLayer,
      customDropper,
      customReOrder: customReOrderElementsLayer,
      customIconLayer,
      fieldConfig: memoizedFieldConfig,
      dndElements: memoizedDndElements,
      cannotDragElements,
      disableLayerDropper,
      disableSaveEdit,
      setDisableSaveEdit,
      customTranslations,
    }),
    [
      editor,
      setEditor,
      propertyEditor,
      setPropertyEditor,
      currentSpec,
      setCurrentSpecWithHistory,
      selectedPath,
      setSelectedPath,
      appendNewElementToSpec,
      removeElementFromSpec,
      reorderElementsInSpec,
      reorderMultipleElementsInSpec,
      undo,
      redo,
      reset,
      canUndo,
      canRedo,
      selectedElement,
      setSelectedElement,
      selectedElements,
      setSelectedElements,
      show,
      setShow,
      language,
      setLanguage,
      designState,
      setDesignState,
      updateElementInSpec,
      customEditElement,
      customDisplayNameLayer,
      customIconLayer,
      customDropper,
      customReOrderElementsLayer,
      memoizedFieldConfig,
      memoizedDndElements,
      cannotDragElements,
      disableLayerDropper,
      disableSaveEdit,
      setDisableSaveEdit,
      customTranslations,
    ]
  )

  return <UiFromSpecContext.Provider value={contextValue}>{children}</UiFromSpecContext.Provider>
}

export const useUiFromSpecContext = () => {
  const context = useContext(UiFromSpecContext)
  if (!context) {
    throw new Error('useUiFromSpecContext must be used within a UiFromSpecProvider')
  }
  return context
}
